/**
 * Product page frame & passepartout configurator.
 * ------------------------------------------------
 * Dropdown/accordion UI (matches the theme size dropdown). After a poster size
 * is chosen it shows two collapsible add-on sections:
 *   1. "Paspartu Ekle"  — every configured mat product that fits the poster size
 *   2. "Çerçeve Ekle"   — frame colours compatible with the size
 *
 * Selecting an option updates the live CSS composite inside the configurator
 * canvas slide and stores the resolved add-on variant ids for `product-form` to
 * add to cart.
 */
if (!customElements.get('product-frame-configurator')) {
  /** Convert a /products.json price (major units string) to integer cents. */
  function toCents(value) {
    if (value == null || value === '') return null;
    const n = parseFloat(value);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  }

  class ProductFrameConfigurator extends HTMLElement {
    constructor() {
      super();
      this.frameProducts = [];
      this.matProducts = [];
      this.loaded = false;
      this.loadingPromise = null;

      this.currentSize = null;
      this.selectedFrameColor = null; // null → no frame
      this.selectedMatKey = null; // product handle, or null → no mat
      // Option the customer picked before choosing a size — replayed by
      // `consumePendingSelection()` as soon as a size exists.
      this.pendingSelection = null;

      this.availableColors = []; // [{ name, token, product, variant, sizeLabel }]
      this.availableMats = []; // [{ key, name, token, product, variant, matSize, ... }]
      this.matForSize = null; // selected entry from availableMats, or null
      this.matGlobalMinCents = null; // cheapest passepartout across every size
      this.frameGlobalMinCents = null; // cheapest frame across every colour & size

      this.boundOnChange = this.onDocumentChange.bind(this);
      this.boundResync = this.scheduleOverlaySync.bind(this);
      this.resyncRaf = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.frameHandle = this.dataset.frameCollectionHandle || '';
      this.matHandle = this.dataset.matCollectionHandle || '';
      this.allowFramelessMat = this.dataset.allowFramelessMat === 'true';
      this.noFrameLabel = this.dataset.noFrameLabel || 'Çerçevesiz';
      this.noMatLabel = this.dataset.noMatLabel || 'Paspartusuz';
      this.fromLabel = this.dataset.fromLabel || '';
      this.fitsLabel = this.dataset.fitsLabel || '';
      this.unavailableLabel = this.dataset.unavailableLabel || 'Stokta yok';

      this.els = {
        matDd: this.querySelector('[data-fc-mat-dd]'),
        matPanel: this.querySelector('[data-fc-mat-panel]'),
        matFrom: this.querySelector('[data-fc-mat-from]'),
        matCurrent: this.querySelector('[data-fc-mat-current]'),
        matIcon: this.querySelector('[data-fc-mat-icon]'),
        matChip: this.querySelector('[data-fc-mat-chip]'),
        matTitle: this.querySelector('[data-fc-mat-title]'),
        frameDd: this.querySelector('[data-fc-frame-dd]'),
        framePanel: this.querySelector('[data-fc-frame-panel]'),
        frameFrom: this.querySelector('[data-fc-frame-from]'),
        frameCurrent: this.querySelector('[data-fc-frame-current]'),
        frameIcon: this.querySelector('[data-fc-frame-icon]'),
        frameChip: this.querySelector('[data-fc-frame-chip]'),
        frameTitle: this.querySelector('[data-fc-frame-title]'),
        matNeedSize: this.querySelector('[data-fc-mat-need-size]'),
        frameNeedSize: this.querySelector('[data-fc-frame-need-size]'),
        overlay: this.querySelector('[data-fc-overlay]'),
        error: this.querySelector('[data-fc-error]'),
      };

      // Titles used for the mobile bottom-sheet headers (captured before the
      // trigger title elements get hidden by a selection).
      this.frameTitleText = this.els.frameTitle ? this.els.frameTitle.textContent.trim() : '';
      this.matTitleText = this.els.matTitle ? this.els.matTitle.textContent.trim() : '';
      this.openKind = null;

      this.addEventListener('click', this.onHostClick.bind(this));
      // Option / sheet-close clicks are bound on the panels + overlay directly
      // because on mobile these get portaled to <body> (see openDropdown) and
      // can no longer rely on delegation from the host element.
      this.boundPanelClick = this.onPanelClick.bind(this);
      this.els.framePanel?.addEventListener('click', this.boundPanelClick);
      this.els.matPanel?.addEventListener('click', this.boundPanelClick);
      this.els.overlay?.addEventListener('click', () => this.closeAllDropdowns());
      document.addEventListener('change', this.boundOnChange, true);
      window.addEventListener('resize', this.boundResync);

      // On mobile the add-on dropdowns open as bottom sheets (matching the size
      // selector). Reset any open sheet when crossing the breakpoint.
      this.mqlMobile = window.matchMedia('(max-width: 749px)');
      this.boundBreakpoint = this.onBreakpointChange.bind(this);
      this.mqlMobile.addEventListener('change', this.boundBreakpoint);

      // Safety net: the fixed bottom-sheet backdrop (+ body-scroll lock) may only
      // exist while a sheet panel is actually active. If it ever lingers — e.g. a
      // race on rapid open/close or a size change mid-sheet — it dims the whole
      // page, follows scrolling and swallows taps ("yarı overlay"). Any pointer /
      // scroll / touch interaction re-checks this invariant and tears down a
      // stray backdrop so it can never block the page.
      this.boundEnforceSheet = this.enforceSheetInvariant.bind(this);
      document.addEventListener('pointerdown', this.boundEnforceSheet, true);
      document.addEventListener('scroll', this.boundEnforceSheet, { passive: true, capture: true });
      window.addEventListener('touchmove', this.boundEnforceSheet, { passive: true });

      this.currentSize = this.readCurrentPosterSize();
      this.boundCustomArt = () => {
        const token = this.selectedColorEntry ? this.selectedColorEntry.token : 'none';
        const mat = this.selectedMatEntry;
        this.updateLivePreview(token, Boolean(mat), mat?.token || 'white');
      };
      document.addEventListener('custom-artwork:changed', this.boundCustomArt);
      this.ensureLoaded().then(() => this.refreshForCurrentSize());
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.boundOnChange, true);
      if (this.boundCustomArt) document.removeEventListener('custom-artwork:changed', this.boundCustomArt);
      window.removeEventListener('resize', this.boundResync);
      this.mqlMobile?.removeEventListener('change', this.boundBreakpoint);
      if (this.boundEnforceSheet) {
        document.removeEventListener('pointerdown', this.boundEnforceSheet, true);
        document.removeEventListener('scroll', this.boundEnforceSheet, { capture: true });
        window.removeEventListener('touchmove', this.boundEnforceSheet);
      }
      document.body.classList.remove('frame-config--sheet-open');
      // Return any portaled sheet/overlay to their original slots.
      this.restoreHome(this.els.framePanel);
      this.restoreHome(this.els.matPanel);
      this.restoreHome(this.els.overlay);
      this.teardownOverlayObservers();
      this.removeCanvasComposite();
    }

    /** Drop the composite from the canvas slide (the slide itself is server HTML). */
    removeCanvasComposite() {
      if (!this.canvasPreview) return;
      window.YarisFramePreview?.unobserveWidth?.(this.canvasPreview);
      this.canvasPreview.remove();
      this.canvasPreview = null;
      this.getCanvasStage()?.classList.remove('is-composed');
    }

    isMobile() {
      return this.mqlMobile ? this.mqlMobile.matches : window.innerWidth < 750;
    }

    onBreakpointChange() {
      // Hard reset so the desktop/mobile presentations never linger mixed.
      this.hardCloseSheet();
    }

    /** True while a bottom-sheet panel is portaled/active (opening or open). This
     *  is the single source of truth for "the fixed backdrop is legitimate". */
    isSheetActive() {
      return [this.els.framePanel, this.els.matPanel].some(
        (p) => p && p.classList.contains('fc-sheet')
      );
    }

    /** Invariant guard: the visible fixed backdrop must be paired with an active
     *  sheet panel. If not (stray/stuck backdrop) tear everything down. Bound to
     *  pointerdown/scroll/touchmove so a stuck overlay clears on first contact. */
    enforceSheetInvariant() {
      const ov = this.els.overlay;
      if (!ov || ov.hidden) return; // backdrop not shown → nothing to police
      if (this.isSheetActive()) return; // legitimately open → leave it
      this.hardCloseSheet();
    }

    /** Fully tear down any open sheet: hide + un-portal both panels and the
     *  backdrop, and release the body-scroll lock. Idempotent. */
    hardCloseSheet() {
      ['frame', 'mat'].forEach((kind) => {
        const dd = kind === 'frame' ? this.els.frameDd : this.els.matDd;
        const panel = kind === 'frame' ? this.els.framePanel : this.els.matPanel;
        if (dd) dd.classList.remove('is-open');
        if (panel) {
          panel.classList.remove('fc-sheet', 'is-in');
          panel.hidden = true;
          this.restoreHome(panel);
        }
        const trg = dd && dd.querySelector('[data-fc-dd-toggle]');
        if (trg) trg.setAttribute('aria-expanded', 'false');
      });
      this.openKind = null;
      if (this.els.overlay) {
        this.els.overlay.hidden = true;
        this.restoreHome(this.els.overlay);
      }
      document.body.classList.remove('frame-config--sheet-open');
    }

    /* ---------------- data loading ---------------- */

    ensureLoaded() {
      if (this.loaded) return Promise.resolve();
      if (this.loadingPromise) return this.loadingPromise;

      const jobs = [];
      if (this.frameHandle) {
        jobs.push(this.fetchCollection(this.frameHandle).then((list) => (this.frameProducts = list)));
      }
      if (this.matHandle) {
        jobs.push(this.fetchCollection(this.matHandle).then((list) => (this.matProducts = this.filterMatProducts(list))));
      }

      this.loadingPromise = Promise.all(jobs)
        .then(() => {
          this.loaded = true;
          this.loadingPromise = null;
        })
        .catch(() => {
          this.loadingPromise = null;
        });
      return this.loadingPromise;
    }

    fetchCollection(handle) {
      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=250`;
      return fetch(url)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) =>
          (data.products || []).map((p) => {
            const optionNames = (p.options || [])
              .map((o) => (typeof o === 'string' ? o : o?.name || ''))
              .filter(Boolean);
            const variants = (p.variants || []).map((v) => {
              const opts =
                v.options && v.options.length
                  ? v.options
                  : [v.option1, v.option2, v.option3].filter((x) => x != null && x !== '');
              return {
                ...v,
                options: opts,
                price: toCents(v.price),
                compare_at_price: toCents(v.compare_at_price),
              };
            });
            return { ...p, optionNames, variants };
          })
        )
        .catch(() => []);
    }

    /** Keep only passepartout products from a mixed accessories collection. */
    filterMatProducts(list) {
      const re = /paspartu|passe-?partout|mat\b|kenarl[iı]k/i;
      return list.filter((p) => {
        const hay = `${p.title || ''} ${p.handle || ''} ${p.product_type || ''} ${(p.tags || []).join(' ')}`;
        return re.test(hay);
      });
    }

    /* ---------------- size handling ---------------- */

    onDocumentChange(e) {
      const root = this.getSectionRoot();
      if (!root || !root.contains(e.target)) return;
      const t = e.target;
      if (
        t.matches('select[name^="options"]') ||
        t.matches('input[type="radio"][name^="options"]') ||
        t.closest('size-option-sheet') ||
        t.closest('variant-selects')
      ) {
        setTimeout(() => this.refreshForCurrentSize(), 0);
      }
    }

    getSectionRoot() {
      return document.querySelector(`#MainProduct-${this.sectionId}`) || document;
    }

    readCurrentPosterSize() {
      const root = this.getSectionRoot();
      if (!root) return null;

      const sheet = root.querySelector('size-option-sheet');
      if (sheet) {
        const select = sheet.querySelector('select');
        const selected = select?.options?.[select.selectedIndex];
        if (select?.value && selected?.dataset?.placeholder !== 'true') return select.value;
        const trig = sheet.querySelector('[data-size-value]')?.getAttribute('data-size-value');
        if (trig) return trig;
      }

      const units = window.YarisSizeUnits;
      const isDim = (name) =>
        units?.isDimensionOptionName ? units.isDimensionOptionName(name) : /boyut|size|ebat|ölçü|olcu/i.test(name);

      const vs = root.querySelector('variant-selects[data-primary]') || root.querySelector('variant-selects');
      if (vs) {
        for (const child of vs.children) {
          const name = child.getAttribute?.('data-option-name') || '';
          const radio = child.querySelector?.('input[type="radio"]:checked');
          if (radio && (isDim(name) || window.YarisFramePreview?.parseSize(radio.value))) return radio.value;
          const select = child.querySelector?.('select[name^="options"]');
          if (select?.value) {
            const opt = select.options[select.selectedIndex];
            if (opt?.dataset?.placeholder !== 'true' && (isDim(name) || window.YarisFramePreview?.parseSize(select.value))) {
              return select.value;
            }
          }
        }
      }
      return null;
    }

    refreshForCurrentSize() {
      if (!this.loaded) {
        this.ensureLoaded().then(() => this.refreshForCurrentSize());
        return;
      }
      this.currentSize = this.readCurrentPosterSize();
      this.computeAvailability(this.currentSize);
      if (this.currentSize) this.consumePendingSelection();
      this.render();
      this.applySelection();
    }

    /** Replay an add-on the customer tapped before picking a size. */
    consumePendingSelection() {
      this.hideNeedSizeHints();
      const pending = this.pendingSelection;
      if (!pending) return;
      this.pendingSelection = null;
      // `render()` drops either selection again if it does not exist at this
      // size, which is the correct graceful outcome.
      if (pending.kind === 'frame') this.selectedFrameColor = pending.value;
      else this.selectedMatKey = pending.value === 'none' ? null : pending.value;
    }

    computeAvailability(size) {
      const FP = window.YarisFramePreview;
      this.matForSize = null;
      this.matUnavailable = false;
      this.availableColors = [];
      this.matGlobalMinCents = this.computeMatGlobalMin();
      if (!size) {
        // No size yet: the rows stay visible and openable (see `render()`), so
        // still build the colour list — just without a size to match against.
        this.rebuildAvailableMats(null);
        this.computeFrameColors(null);
        return;
      }

      // Passepartout: every product in the configured collection that is a mat
      // is a selectable colour. Size mapping can mark a row unavailable, but
      // must not hide the other colours (siyah / kahverengi used to vanish
      // because only the first matching product was kept).
      this.rebuildAvailableMats(size);

      // Frames are computed against the current frame target size in render().
      this.computeFrameColors(this.frameTargetSize());
    }

    /** Frame must match the passepartout size when a mat is chosen, else poster. */
    frameTargetSize() {
      const mat = this.selectedMatEntry;
      return mat && !mat.unavailable ? mat.matSize : this.currentSize;
    }

    matKeyFor(product) {
      return String(product.handle || product.id || product.title || '');
    }

    resolveMatToken(name, handle) {
      const hay = `${name || ''} ${handle || ''}`;
      if (/keten|linen/i.test(hay)) return 'linen';
      if (/kahverengi|brown|walnut|ceviz/i.test(hay)) return 'brown';
      if (/siyah|black|antrasit/i.test(hay)) return 'black';
      if (/krem|cream|ekru|ivory/i.test(hay)) return 'white';
      if (/beyaz|white/i.test(hay)) return 'white';
      return window.YarisFramePreview?.resolveMatToken?.(hay) || 'white';
    }

    rebuildAvailableMats(size) {
      const FP = window.YarisFramePreview;
      this.availableMats = [];
      this.matForSize = null;
      this.matUnavailable = false;
      if (!this.matProducts.length) return;

      const classicMatSize = size && FP?.matSizeFor ? FP.matSizeFor(size) : null;
      for (const product of this.matProducts) {
        const sizeIdx = this.findSizeOptionIndex(product);
        const layout = FP?.resolveMatLayout?.(`${product.title || ''} ${product.handle || ''}`) || null;
        let matSize = null;
        let matchLabel = null;
        let match = null;
        if (layout) {
          if (size && FP.layoutFitsPoster?.(layout, size)) {
            const outerLabel = `${layout.outer[0]}x${layout.outer[1]}`;
            match =
              product.variants.find((v) => {
                if (v.available === false) return false;
                const vSize = sizeIdx >= 0 ? v.options[sizeIdx] : this.firstSizeLikeOption(v);
                return vSize && FP.sizeValuesMatch(vSize, outerLabel);
              }) || product.variants.find((v) => v.available !== false);
            if (match) {
              matchLabel = outerLabel;
              matSize = outerLabel;
            }
          }
        } else if (classicMatSize) {
          matSize = classicMatSize;
          match = product.variants.find((v) => {
            if (v.available === false) return false;
            const vSize = sizeIdx >= 0 ? v.options[sizeIdx] : this.firstSizeLikeOption(v);
            if (vSize && FP.sizeValuesMatch(vSize, matSize)) {
              matchLabel = vSize;
              return true;
            }
            return false;
          });
        }
        let globalMin = null;
        for (const v of product.variants) {
          if (v.available === false || v.price == null) continue;
          const price = Number(v.price);
          if (Number.isFinite(price) && (globalMin == null || price < globalMin)) globalMin = price;
        }
        const unavailable = Boolean(size) && (!matSize || !match);
        this.availableMats.push({
          key: this.matKeyFor(product),
          name: product.title || product.handle || 'Paspartu',
          token: this.resolveMatToken(product.title, product.handle),
          product,
          variant: match || null,
          matSize: match ? matSize : null,
          matSizeLabel: matchLabel || matSize || '',
          unavailable,
          globalMinCents: globalMin,
        });
      }

      if (this.selectedMatKey) {
        const selected = this.availableMats.find((m) => m.key === this.selectedMatKey);
        if (selected && !selected.unavailable) this.matForSize = selected;
        else if (size) this.selectedMatKey = selected?.unavailable ? this.selectedMatKey : null;
      }
      this.matUnavailable = Boolean(size) && this.availableMats.every((m) => m.unavailable);
    }

    computeFrameColors(targetSize) {
      const FP = window.YarisFramePreview;
      this.availableColors = [];
      this.frameGlobalMinCents = null;
      if (!this.frameProducts.length) return;

      // Collect every known frame colour first, then mark size-incompatible
      // entries as unavailable so the option stays visible.
      const colorMap = new Map();
      for (const product of this.frameProducts) {
        const sizeIdx = this.findSizeOptionIndex(product);
        for (const v of product.variants) {
          const colorName = this.colorOptionValue(product, v, sizeIdx);
          const key = (colorName || product.title || product.handle).toLowerCase();
          if (!colorMap.has(key)) {
            colorMap.set(key, {
              name: colorName || product.title,
              token: FP.resolveFrameToken(colorName || product.title),
              product,
              variant: null,
              sizeLabel: null,
              unavailable: true,
              // Cheapest in-stock variant of this colour across every size —
              // what the row advertises while no size is chosen.
              globalMinCents: null,
            });
          }
          const entry = colorMap.get(key);
          if (v.available !== false && v.price != null) {
            const price = Number(v.price);
            if (Number.isFinite(price) && (entry.globalMinCents == null || price < entry.globalMinCents)) {
              entry.globalMinCents = price;
            }
            if (Number.isFinite(price) && (this.frameGlobalMinCents == null || price < this.frameGlobalMinCents)) {
              this.frameGlobalMinCents = price;
            }
          }
        }
      }

      if (targetSize) {
        for (const entry of colorMap.values()) {
          const sizeIdx = this.findSizeOptionIndex(entry.product);
          const match = entry.product.variants.find((v) => {
            if (v.available === false) return false;
            const vSize = sizeIdx >= 0 ? v.options[sizeIdx] : this.firstSizeLikeOption(v);
            return vSize && FP.sizeValuesMatch(vSize, targetSize);
          });
          if (match) {
            const vSize = sizeIdx >= 0 ? match.options[sizeIdx] : this.firstSizeLikeOption(match);
            entry.variant = match;
            entry.sizeLabel = vSize;
            entry.unavailable = false;
          }
        }
      } else {
        // Without a size, "unavailable" is unknowable — greying every colour out
        // would defeat the point of showing the row early. So present each colour
        // that has at least one in-stock variant somewhere in the catalogue as
        // pickable; tapping it asks for a size and is replayed afterwards.
        for (const entry of colorMap.values()) {
          entry.unavailable = entry.globalMinCents == null;
          entry.pending = !entry.unavailable;
        }
      }

      this.availableColors = Array.from(colorMap.values());
    }

    /** Cheapest in-stock passepartout variant across every mat size. */
    computeMatGlobalMin() {
      let min = null;
      for (const product of this.matProducts) {
        for (const v of product.variants) {
          if (v.available === false || v.price == null) continue;
          const price = Number(v.price);
          if (Number.isFinite(price) && (min == null || price < min)) min = price;
        }
      }
      return min;
    }

    findSizeOptionIndex(product) {
      const units = window.YarisSizeUnits;
      const names = product.optionNames || [];
      for (let i = 0; i < names.length; i += 1) {
        if (units?.isDimensionOptionName ? units.isDimensionOptionName(names[i]) : /boyut|size|ebat|ölçü|olcu/i.test(names[i])) {
          return i;
        }
      }
      for (let i = 0; i < names.length; i += 1) {
        const sample = product.variants[0]?.options?.[i];
        if (sample && window.YarisFramePreview?.parseSize(sample)) return i;
      }
      return -1;
    }

    firstSizeLikeOption(variant) {
      for (const opt of variant.options || []) {
        if (window.YarisFramePreview?.parseSize(opt)) return opt;
      }
      return null;
    }

    colorOptionValue(product, variant, sizeIdx) {
      const names = product.optionNames || [];
      for (let i = 0; i < names.length; i += 1) {
        if (i === sizeIdx) continue;
        if (/renk|color|colour/i.test(names[i]) && variant.options[i]) return variant.options[i];
      }
      for (let i = 0; i < names.length; i += 1) {
        if (i === sizeIdx) continue;
        if (variant.options[i]) return variant.options[i];
      }
      return product.title || '';
    }

    /* ---------------- rendering ---------------- */

    render() {
      // Recompute frame colours against the current target size (poster size, or
      // the mat size when a passepartout is selected).
      this.computeFrameColors(this.frameTargetSize());

      const hasSize = Boolean(this.currentSize);
      const hasFrames = this.availableColors.length > 0;
      const hasMatCatalog = this.matProducts.length > 0;
      // "SEÇENEKLER" is shown as soon as the catalogues loaded, whether or not a
      // size is chosen — a size is only required to actually apply a selection.
      const showMat = hasMatCatalog;
      const showFrame = hasFrames;

      if (this.els.matDd) this.els.matDd.hidden = !showMat;
      if (this.els.frameDd) this.els.frameDd.hidden = !showFrame;
      this.hidden = !(showFrame || showMat);

      // Size cleared while something was selected → park the selection so it is
      // replayed on the next size instead of claiming an add-on we cannot resolve.
      if (!hasSize && (this.selectedFrameColor || this.selectedMatKey)) {
        if (this.selectedFrameColor) {
          this.pendingSelection = { kind: 'frame', value: this.selectedFrameColor };
        } else {
          this.pendingSelection = { kind: 'mat', value: this.selectedMatKey };
        }
        this.selectedFrameColor = null;
        this.selectedMatKey = null;
        this.matForSize = null;
      }

      // Reset selections that no longer apply.
      if (
        this.selectedFrameColor &&
        !this.availableColors.some((c) => c.name === this.selectedFrameColor && !c.unavailable)
      ) {
        this.selectedFrameColor = null;
      }
      if (
        this.selectedMatKey &&
        !this.availableMats.some((m) => m.key === this.selectedMatKey && !m.unavailable)
      ) {
        this.selectedMatKey = null;
        this.matForSize = null;
      }

      if (showFrame) this.renderFrameOptions();
      if (showMat) this.renderMatOptions();
      window.YarisSizeUnits?.applyAll?.();
    }

    renderFrameOptions() {
      const panel = this.els.framePanel;
      if (!panel) return;
      panel.innerHTML = '';
      panel.appendChild(this.buildSheetHead(this.frameTitleText));
      panel.appendChild(
        this.buildOption({ kind: 'frame', value: 'none', token: 'none', name: this.noFrameLabel })
      );
      const hasSize = Boolean(this.currentSize);
      let min = null;
      for (const c of this.availableColors) {
        // With a size we quote that size's variant; without one we quote the
        // colour's cheapest variant across all sizes, so the row's "başlangıç"
        // becomes the global minimum across every frame variant.
        const price = hasSize
          ? !c.unavailable && c.variant
            ? Number(c.variant.price)
            : null
          : c.globalMinCents;
        if (price != null && (min == null || price < min)) min = price;
        panel.appendChild(
          this.buildOption({
            kind: 'frame',
            value: c.name,
            token: c.token,
            name: c.name,
            meta: c.unavailable ? this.unavailableLabel : '',
            metaSize: c.unavailable || !hasSize ? '' : c.sizeLabel || this.currentSize,
            price,
            unavailable: Boolean(c.unavailable),
          })
        );
      }
      this.frameMinCents = min;
      this.updateFrameSummary();
    }

    renderMatOptions() {
      const panel = this.els.matPanel;
      if (!panel) return;
      panel.innerHTML = '';
      panel.appendChild(this.buildSheetHead(this.matTitleText));
      panel.appendChild(this.buildOption({ kind: 'mat', value: 'none', token: 'none', name: this.noMatLabel }));

      // No size yet → keep the option pickable and quote the cheapest mat across
      // every size (same idea as the frame row).
      const hasSize = Boolean(this.currentSize);
      let min = null;
      for (const m of this.availableMats) {
        const price = hasSize
          ? !m.unavailable && m.variant
            ? Number(m.variant.price)
            : null
          : m.globalMinCents;
        if (price != null && (min == null || price < min)) min = price;
        panel.appendChild(
          this.buildOption({
            kind: 'mat',
            value: m.key,
            token: m.token,
            name: m.name,
            meta: m.unavailable ? this.unavailableLabel : '',
            metaSize: m.unavailable || !hasSize ? '' : m.matSizeLabel || '',
            metaFits: m.unavailable || !hasSize ? '' : this.currentSize,
            price,
            unavailable: Boolean(m.unavailable),
          })
        );
      }
      this.matMinCents = min;
      this.updateMatSummary();
    }

    matMetaText() {
      const parts = [];
      if (this.matForSize?.matSizeLabel) parts.push(this.matForSize.matSizeLabel);
      if (this.fitsLabel && this.currentSize) parts.push(`${this.fitsLabel}: ${this.currentSize}`);
      return parts.join(' · ');
    }

    /** Convert a size label through the page's single YarisSizeUnits state. */
    formatSizeLabel(original) {
      if (!original) return '';
      const units = window.YarisSizeUnits;
      if (!units?.displayText) return original;
      return units.displayText(original, units.getUnit());
    }

    appendSizeValue(parent, original) {
      if (!parent || !original) return;
      const wrap = document.createElement('span');
      wrap.setAttribute('data-size-value', original);
      const display = document.createElement('span');
      display.setAttribute('data-size-display', '');
      display.textContent = this.formatSizeLabel(original);
      wrap.appendChild(display);
      parent.appendChild(wrap);
    }

    writeSizeMeta(el, { size, fits, fallback } = {}) {
      if (!el) return;
      el.textContent = '';
      if (size) this.appendSizeValue(el, size);
      if (fits && this.fitsLabel) {
        if (el.childNodes.length) el.appendChild(document.createTextNode(' · '));
        el.appendChild(document.createTextNode(`${this.fitsLabel}: `));
        this.appendSizeValue(el, fits);
      }
      if (!el.childNodes.length && fallback) el.textContent = fallback;
    }

    buildOption({ kind, value, token, name, meta, metaSize, metaFits, price, unavailable }) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'frame-config__opt';
      btn.setAttribute('data-fc-opt', '');
      btn.setAttribute('data-kind', kind);
      btn.setAttribute('data-value', value);
      if (unavailable) {
        btn.classList.add('is-unavailable');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      }

      const chip = document.createElement('span');
      chip.className = 'frame-config__opt-chip';
      chip.setAttribute('data-frame', token);
      btn.appendChild(chip);

      const textWrap = document.createElement('span');
      textWrap.className = 'frame-config__opt-text';
      const nameEl = document.createElement('span');
      nameEl.className = 'frame-config__opt-name';
      nameEl.textContent = name || '';
      textWrap.appendChild(nameEl);
      if (meta || metaSize || metaFits) {
        const metaEl = document.createElement('span');
        metaEl.className = 'frame-config__opt-meta';
        if (metaSize || metaFits) {
          this.writeSizeMeta(metaEl, { size: metaSize, fits: metaFits, fallback: meta });
        } else {
          metaEl.textContent = meta || '';
        }
        textWrap.appendChild(metaEl);
      }
      btn.appendChild(textWrap);

      const priceEl = document.createElement('span');
      priceEl.className = 'frame-config__opt-price';
      priceEl.textContent =
        unavailable ? this.unavailableLabel : price != null && price > 0 ? this.formatMoney(price) : '';
      btn.appendChild(priceEl);

      return btn;
    }

    /** Header shown at the top of a mobile bottom-sheet panel (title + close). */
    buildSheetHead(title) {
      const head = document.createElement('div');
      head.className = 'frame-config__sheet-head';

      const label = document.createElement('span');
      label.className = 'frame-config__sheet-title';
      label.textContent = title || '';
      head.appendChild(label);

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'frame-config__sheet-close';
      close.setAttribute('data-fc-sheet-close', '');
      close.setAttribute('aria-label', 'Kapat');
      head.appendChild(close);

      return head;
    }

    fromText(cents) {
      const money = this.formatMoney(cents);
      return this.fromLabel ? `${this.fromLabel} ${money}` : money;
    }

    updateFrameSummary() {
      const entry = this.selectedColorEntry;
      const sel = Boolean(entry);
      const e = this.els;
      if (e.frameIcon) e.frameIcon.hidden = sel;
      if (e.frameChip) {
        e.frameChip.hidden = !sel;
        if (sel) e.frameChip.setAttribute('data-frame', entry.token);
      }
      if (e.frameTitle) e.frameTitle.hidden = sel;
      this.setCurrent(e.frameCurrent, sel ? entry.name : '', '', {
        size: sel ? entry.sizeLabel || this.currentSize : '',
      });
      if (e.frameFrom) {
        const cents = sel ? Number(entry.variant?.price) : this.frameMinCents;
        e.frameFrom.textContent =
          cents != null && cents > 0 ? (sel ? this.formatMoney(cents) : this.fromText(cents)) : '';
      }
      if (e.frameDd) e.frameDd.classList.toggle('is-selected', sel);
      this.markSelectedOption('frame', sel ? entry.name : 'none');
    }

    updateMatSummary() {
      const entry = this.selectedMatEntry;
      const on = Boolean(entry);
      const e = this.els;
      if (e.matIcon) e.matIcon.hidden = on;
      if (e.matChip) {
        e.matChip.hidden = !on;
        if (on) e.matChip.setAttribute('data-frame', entry.token || 'white');
      }
      if (e.matTitle) e.matTitle.hidden = on;
      this.setCurrent(e.matCurrent, on ? entry.name || 'Paspartu' : '', '', {
        size: on ? entry.matSizeLabel || '' : '',
        fits: on ? this.currentSize : '',
      });
      if (e.matFrom) {
        const cents = on ? Number(entry.variant?.price) : this.matMinCents;
        e.matFrom.textContent =
          cents != null && cents > 0 ? (on ? this.formatMoney(cents) : this.fromText(cents)) : '';
      }
      if (e.matDd) e.matDd.classList.toggle('is-selected', on);
      this.markSelectedOption('mat', on ? entry.key : 'none');
    }

    /** Populate a trigger's inline "current selection" (name + muted meta). */
    setCurrent(el, name, meta, sizeMeta) {
      if (!el) return;
      if (!name) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      el.hidden = false;
      el.textContent = '';
      const nameEl = document.createElement('span');
      nameEl.className = 'frame-config__dd-current-name';
      nameEl.textContent = name;
      el.appendChild(nameEl);
      if ((sizeMeta && (sizeMeta.size || sizeMeta.fits)) || meta) {
        const metaEl = document.createElement('span');
        metaEl.className = 'frame-config__dd-current-meta';
        if (sizeMeta && (sizeMeta.size || sizeMeta.fits)) {
          this.writeSizeMeta(metaEl, sizeMeta);
        } else {
          metaEl.textContent = meta;
        }
        el.appendChild(metaEl);
      }
    }

    markSelectedOption(kind, value) {
      const panel = kind === 'frame' ? this.els.framePanel : this.els.matPanel;
      if (!panel) return;
      panel.querySelectorAll('[data-fc-opt]').forEach((el) => {
        const sel = el.getAttribute('data-value') === value;
        el.classList.toggle('is-selected', sel);
        el.setAttribute('aria-pressed', sel ? 'true' : 'false');
      });
    }

    /* ---------------- interaction ---------------- */

    onHostClick(e) {
      const toggle = e.target.closest('[data-fc-dd-toggle]');
      if (toggle && this.contains(toggle)) {
        e.preventDefault();
        this.toggleDropdown(toggle.getAttribute('data-fc-dd-toggle'));
      }
    }

    /** Option selection + in-sheet close button. Bound on the panel itself so it
     *  still works once the panel is portaled to <body> on mobile. */
    onPanelClick(e) {
      if (e.target.closest('[data-fc-sheet-close]')) {
        e.preventDefault();
        this.closeAllDropdowns();
        return;
      }
      const opt = e.target.closest('[data-fc-opt]');
      if (!opt || opt.disabled || opt.classList.contains('is-unavailable')) return;
      e.preventDefault();
      const kind = opt.getAttribute('data-kind');
      const value = opt.getAttribute('data-value');

      // A real add-on variant can only be resolved once a poster size is known.
      // Remember what was picked, send the customer to the BOYUT selector, and
      // replay the choice from `consumePendingSelection()` afterwards.
      if (!this.currentSize && value !== 'none') {
        this.pendingSelection = { kind, value };
        this.closeDropdown(kind);
        this.promptForSize(kind);
        return;
      }

      if (kind === 'frame') this.selectFrame(value === 'none' ? null : value);
      else this.selectMat(value === 'none' ? null : value);
      this.closeDropdown(kind);
    }

    /** Ask for a poster size: inline note on the row + open/focus the selector. */
    promptForSize(kind) {
      const note = kind === 'frame' ? this.els.frameNeedSize : this.els.matNeedSize;
      this.hideNeedSizeHints();
      if (note) note.hidden = false;

      const sheet = this.getSectionRoot().querySelector('size-option-sheet');
      if (!sheet) return;
      const trigger = sheet.querySelector('.size-option-sheet__trigger');
      trigger?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (sheet.classList.contains('size-option-sheet--mobile')) {
        sheet.open?.();
      } else {
        sheet.querySelector('select')?.focus();
      }
    }

    hideNeedSizeHints() {
      if (this.els?.frameNeedSize) this.els.frameNeedSize.hidden = true;
      if (this.els?.matNeedSize) this.els.matNeedSize.hidden = true;
    }

    closeAllDropdowns() {
      this.closeDropdown('frame');
      this.closeDropdown('mat');
    }

    toggleDropdown(kind) {
      const dd = kind === 'frame' ? this.els.frameDd : this.els.matDd;
      if (!dd) return;
      const open = dd.classList.contains('is-open');
      // Accordion: close both, then open the requested one if it was closed.
      this.closeDropdown('frame');
      this.closeDropdown('mat');
      if (!open) this.openDropdown(kind);
    }

    openDropdown(kind) {
      const dd = kind === 'frame' ? this.els.frameDd : this.els.matDd;
      const panel = kind === 'frame' ? this.els.framePanel : this.els.matPanel;
      if (!dd || !panel) return;
      dd.classList.add('is-open');
      panel.hidden = false;
      const trg = dd.querySelector('[data-fc-dd-toggle]');
      if (trg) trg.setAttribute('aria-expanded', 'true');
      this.openKind = kind;

      // Mobile: portal the overlay + panel to <body> so `position: fixed`
      // resolves against the viewport (not a transformed ancestor), then slide
      // the sheet up from the bottom on the next frame.
      if (this.isMobile()) {
        this.moveToBody(this.els.overlay);
        this.moveToBody(panel);
        panel.classList.add('fc-sheet');
        if (this.els.overlay) this.els.overlay.hidden = false;
        document.body.classList.add('frame-config--sheet-open');
        requestAnimationFrame(() => panel.classList.add('is-in'));
      }
    }

    closeDropdown(kind) {
      const dd = kind === 'frame' ? this.els.frameDd : this.els.matDd;
      const panel = kind === 'frame' ? this.els.framePanel : this.els.matPanel;
      if (!dd || !panel) return;
      const trg = dd.querySelector('[data-fc-dd-toggle]');
      if (trg) trg.setAttribute('aria-expanded', 'false');
      if (this.openKind === kind) this.openKind = null;

      if (panel.classList.contains('fc-sheet')) {
        // Slide the sheet down, then tidy up (un-portal) once the transition
        // finishes — unless it was reopened in the meantime.
        panel.classList.remove('is-in');
        if (!this.openKind) {
          if (this.els.overlay) this.els.overlay.hidden = true;
          document.body.classList.remove('frame-config--sheet-open');
        }
        setTimeout(() => {
          if (panel.classList.contains('is-in')) return;
          dd.classList.remove('is-open');
          panel.hidden = true;
          panel.classList.remove('fc-sheet');
          this.restoreHome(panel);
          if (!this.openKind) this.restoreHome(this.els.overlay);
        }, 320);
        return;
      }

      dd.classList.remove('is-open');
      panel.hidden = true;
    }

    /* ---- portal helpers (the bottom sheet must escape transformed ancestors) */
    moveToBody(el) {
      if (!el || el._fcInBody) return;
      el._fcHome = { parent: el.parentNode, next: el.nextSibling };
      document.body.appendChild(el);
      el._fcInBody = true;
    }

    restoreHome(el) {
      if (!el || !el._fcInBody) return;
      const home = el._fcHome;
      if (home && home.parent) home.parent.insertBefore(el, home.next || null);
      el._fcInBody = false;
    }

    selectFrame(colorName) {
      this.selectedFrameColor = colorName;
      this.updateFrameSummary();
      this.applySelection();
    }

    selectMat(key) {
      this.selectedMatKey = key;
      const selected = this.availableMats.find((m) => m.key === key && !m.unavailable);
      this.matForSize = selected || null;
      // Frame size is tied to the mat size, so re-render frame options/prices.
      this.render();
      this.applySelection();
    }

    get selectedMatEntry() {
      if (!this.selectedMatKey || !this.currentSize) return null;
      return this.availableMats.find((m) => m.key === this.selectedMatKey && !m.unavailable) || null;
    }

    /* ---------------- selection + cart ---------------- */

    get selectedColorEntry() {
      // Without a size no variant can be resolved, so there is no live selection
      // (the pick is parked in `pendingSelection` instead).
      if (!this.selectedFrameColor || !this.currentSize) return null;
      return (
        this.availableColors.find((c) => c.name === this.selectedFrameColor && !c.unavailable) || null
      );
    }

    applySelection() {
      const entry = this.selectedColorEntry;
      const token = entry ? entry.token : 'none';
      const mat = this.selectedMatEntry;
      this.updateLivePreview(token, Boolean(mat), mat?.token || 'white');
      this.publishSelection();
    }

    getSelectedAddonVariantIds() {
      const ids = [];
      const mat = this.selectedMatEntry;
      const matActive = Boolean(mat);
      const entry = this.selectedColorEntry;

      if (entry) {
        const targetSize = matActive && mat.matSize ? mat.matSize : this.currentSize;
        const frameVariant = this.resolveFrameVariant(entry.product, targetSize) || entry.variant;
        if (frameVariant) ids.push(frameVariant.id);
      }
      if (matActive && mat?.variant) ids.push(mat.variant.id);
      return ids;
    }

    /** Total price (cents) of the currently selected frame + passepartout. */
    getSelectedAddonTotalCents() {
      let total = 0;
      const mat = this.selectedMatEntry;
      const matActive = Boolean(mat);
      const entry = this.selectedColorEntry;
      if (entry) {
        const targetSize = matActive && mat.matSize ? mat.matSize : this.currentSize;
        const frameVariant = this.resolveFrameVariant(entry.product, targetSize) || entry.variant;
        if (frameVariant && frameVariant.price != null) total += Number(frameVariant.price) || 0;
      }
      if (matActive && mat?.variant?.price != null) {
        total += Number(mat.variant.price) || 0;
      }
      return total;
    }

    resolveFrameVariant(product, size) {
      const FP = window.YarisFramePreview;
      const sizeIdx = this.findSizeOptionIndex(product);
      return (
        product.variants.find((v) => {
          if (v.available === false) return false;
          const vSize = sizeIdx >= 0 ? v.options[sizeIdx] : this.firstSizeLikeOption(v);
          return vSize && FP.sizeValuesMatch(vSize, size);
        }) || null
      );
    }

    publishSelection() {
      this.dataset.hasFrame = this.selectedColorEntry ? 'true' : 'false';
      this.dispatchEvent(
        new CustomEvent('frame-config:change', {
          bubbles: true,
          detail: {
            frameColor: this.selectedFrameColor,
            mat: Boolean(this.selectedMatEntry),
            matKey: this.selectedMatKey,
            matToken: this.selectedMatEntry?.token || null,
            addonVariantIds: this.getSelectedAddonVariantIds(),
            addonTotalCents: this.getSelectedAddonTotalCents(),
          },
        })
      );
    }

    /* ---------------- live canvas-slide preview ---------------- */

    getGallery() {
      return document.getElementById(this.dataset.galleryId || `GalleryViewer-${this.sectionId}`);
    }

    /* ---- configurator canvas slide (`custom.design_artwork` products) ----
     * The canvas slide is the only live-preview surface: it provides the studio
     * backdrop and holds the raw artwork. Product photos are never touched, so
     * products without the metafield simply get no live preview. */

    getCanvasStage() {
      if (this.canvasStage && this.canvasStage.isConnected) return this.canvasStage;
      const gallery = this.getGallery();
      this.canvasStage = gallery ? gallery.querySelector('[data-fc-canvas] [data-fc-canvas-stage]') : null;
      return this.canvasStage;
    }

    /** Aspect ratio (w/h) of the raw artwork. Liquid ships the true file
     *  dimensions so this is correct before the image has decoded. */
    canvasArtRatio(art) {
      if (!art) return 1;
      const w = Number(art.dataset.artWidth) || art.naturalWidth;
      const h = Number(art.dataset.artHeight) || art.naturalHeight;
      return w > 0 && h > 0 ? w / h : 1;
    }

    /**
     * Render into the canvas slide. Returns false when there is no canvas slide
     * (products without the `custom.design_artwork` metafield).
     */
    updateCanvasPreview(token, matActive, framelessMat, matToken) {
      const stage = this.getCanvasStage();
      if (!stage) return false;

      const art = stage.querySelector('[data-fc-canvas-art]');
      // Bare artwork on the studio backdrop: the honest state while no size is
      // chosen, and also the "no frame / no mat" look.
      if (!this.currentSize || (token === 'none' && !framelessMat)) {
        stage.classList.remove('is-composed');
        if (this.canvasPreview) this.canvasPreview.hidden = true;
        this.previewState = null;
        return true;
      }

      if (!this.canvasPreview || !stage.contains(this.canvasPreview)) {
        this.canvasPreview = window.YarisFramePreview.buildPreview({ frameToken: 'none' });
        this.canvasPreview.classList.add('fc-canvas__composite');
        stage.appendChild(this.canvasPreview);
        this.setupOverlayObservers(this.getGallery());
      }
      this.canvasPreview.hidden = false;
      stage.classList.add('is-composed');

      const ratio = this.canvasArtRatio(art);
      const src = art ? art.currentSrc || art.src : '';
      const prev = this.previewState;
      // Custom editor pushes a new bitmap every crop/field change. Re-animate
      // only when the frame / mat actually changes — otherwise the composite
      // flickers while the customer pans.
      const artOnly =
        prev &&
        prev.token === token &&
        prev.matActive === matActive &&
        (prev.matToken || 'white') === (matToken || 'white');
      this.previewState = { token, matActive, matToken: matToken || 'white', src, ratio, crop: null };
      stage.style.setProperty('--fc-canvas-ratio', String(ratio));
      // The metafield artwork is the raw design (no studio margins baked in), so
      // there is nothing to crop-detect — unlike the legacy mockup photos.
        const matEntry = this.selectedMatEntry;
        window.YarisFramePreview.updatePreview(this.canvasPreview, {
        frameToken: token,
        mat: matActive,
        matToken: matToken || 'white',
        matLabel: matEntry ? `${matEntry.name || ''} ${matEntry.product?.handle || ''}` : '',
        allowMatWithoutFrame: framelessMat,
        imgSrc: src,
        alt: art ? art.alt || '' : '',
        ratio,
        crop: null,
      });
      this.syncCanvasSize();
      if (!artOnly) this.animateIn(this.canvasPreview);
      return true;
    }

    /**
     * Fit the composite inside the studio box.
     *
     * The window keeps the artwork ratio, so the frame moulding and the mat
     * band add height on top of it. Sizing by width alone would push a portrait
     * composite past the bottom of the gray area, so the width is solved from
     * whichever of the two axes runs out first. Both bands are percentages of
     * the composite width (CSS padding %), which makes the total height a plain
     * multiple of that width.
     */
    syncCanvasSize() {
      const stage = this.getCanvasStage();
      if (!stage || !this.canvasPreview || this.canvasPreview.hidden) return;

      const art = stage.querySelector('[data-fc-canvas-art]');
      const ratio = this.previewState?.ratio || this.canvasArtRatio(art);
      this.canvasPreview.style.setProperty('--fp-ratio', String(ratio));
      stage.style.setProperty('--fc-canvas-ratio', String(ratio));

      const styles = getComputedStyle(stage);
      const availWidth =
        stage.clientWidth - parseFloat(styles.paddingLeft || 0) - parseFloat(styles.paddingRight || 0);
      const availHeight =
        stage.clientHeight - parseFloat(styles.paddingTop || 0) - parseFloat(styles.paddingBottom || 0);
      if (!(availWidth > 0) || !(availHeight > 0) || !(ratio > 0)) return;

      const scale = parseFloat(styles.getPropertyValue('--fc-canvas-scale')) || 0.8;
      const frameBand = 0.021;
      const matWidth = 1 - 2 * frameBand;
      const outer = this.canvasPreview.classList.contains('has-mat-layout')
        ? String(this.canvasPreview.dataset.matOuter || '')
        : '';
      const outerParts = outer.split('/').map((n) => Number(n));
      let heightPerWidth;
      if (outerParts.length === 2 && outerParts[0] > 0 && outerParts[1] > 0) {
        heightPerWidth = matWidth * (outerParts[1] / outerParts[0]) + 2 * frameBand;
      } else {
        const matBand = this.canvasPreview.classList.contains('is-matted') ? 0.11 : 0;
        const windowWidth = matWidth * (1 - 2 * matBand);
        heightPerWidth = windowWidth / ratio + 2 * matBand * matWidth + 2 * frameBand;
      }

      const width = Math.min(availWidth, availHeight / heightPerWidth) * scale;
      this.canvasPreview.style.width = `${Math.max(0, Math.round(width))}px`;
    }

    /** Without a canvas slide there is no preview surface at all: drop any
     *  composite left over from a previous gallery render and stay inert. */
    updateLivePreview(token, matActive, matToken) {
      const framelessMat = token === 'none' && matActive && this.allowFramelessMat;
      if (this.updateCanvasPreview(token, matActive, framelessMat, matToken || 'white')) return;
      this.removeCanvasComposite();
      this.previewState = null;
    }

    animateIn(el) {
      if (!el || typeof el.animate !== 'function') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      try {
        el.animate(
          [
            { opacity: 0.35, transform: 'translate(-50%, -50%) scale(0.985)' },
            { opacity: 1, transform: 'translate(-50%, -50%)' },
          ],
          { duration: 620, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
        );
      } catch (e) {
        /* no-op */
      }
    }

    setupOverlayObservers(gallery) {
      if (!gallery) return;
      this.teardownOverlayObservers();
      this.galleryEl = gallery;
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.boundResync);
        this.ro.observe(gallery);
      }
      this.mo = new MutationObserver(this.boundResync);
      const list = gallery.querySelector('.product__media-list');
      if (list) {
        this.mo.observe(list, { attributes: true, subtree: true, attributeFilter: ['class'] });
        list.addEventListener('scroll', this.boundResync, { passive: true });
        this.scrollListEl = list;
      }
    }

    teardownOverlayObservers() {
      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }
      if (this.mo) {
        this.mo.disconnect();
        this.mo = null;
      }
      if (this.scrollListEl) {
        this.scrollListEl.removeEventListener('scroll', this.boundResync);
        this.scrollListEl = null;
      }
    }

    scheduleOverlaySync() {
      if (this.resyncRaf) cancelAnimationFrame(this.resyncRaf);
      this.resyncRaf = requestAnimationFrame(() => {
        this.resyncRaf = null;
        // Only the composite's fitted width can change.
        if (this.canvasPreview && !this.canvasPreview.hidden) this.syncCanvasSize();
      });
    }

    /* ---------------- utils ---------------- */

    formatMoney(cents) {
      if (window.YarisMoney) return window.YarisMoney.format(cents);
      const amount = Number(cents) / 100;
      return Number.isFinite(amount) ? `${amount.toFixed(2)} TL` : '';
    }
  }

  customElements.define('product-frame-configurator', ProductFrameConfigurator);
}
